import { supabase } from './supabase.js';

function pickComunidadImage(cuerpo) {
  if (!cuerpo || typeof cuerpo !== 'object') return null;
  const u = String(cuerpo.imagenUrl ?? cuerpo.imagen_url ?? '').trim();
  return u || null;
}

function listingPreviewFields(row) {
  const artist = String(row.artist ?? '').trim();
  const title = String(row.title ?? '').trim();
  const main = artist && title ? `${artist} — ${title}` : title || artist || 'Sin título';
  const price = row.price != null ? `${Number(row.price).toFixed(2)} €` : null;
  const isAuction = !!row.is_auction;
  const subtitle = [
    isAuction ? 'Subasta' : 'Mercado',
    price ? (isAuction ? `desde ${price}` : price) : null,
    row.format ? String(row.format) : null,
    row.status && row.status !== 'active' ? `estado: ${row.status}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return {
    title: main,
    subtitle: subtitle || null,
    imageUrl: row.cover_url ? String(row.cover_url) : null,
  };
}

/** Clave estable para el mapa de vistas previas. */
export function queuePreviewKey(contentType, contentId) {
  return `${contentType}:${contentId}`;
}

/**
 * Carga títulos e imágenes para filas de `social_publish_queue` (admin).
 * @param {Array<{ content_type: string, content_id: string }>} rows
 * @returns {Promise<Map<string, { title: string, subtitle?: string|null, extracto?: string|null, imageUrl?: string|null, missing?: boolean }>>}
 */
export async function fetchSocialQueuePreviews(rows) {
  const map = new Map();
  if (!rows?.length) return map;

  const ids = { noticia: [], comunidad: [], listing: [] };
  for (const r of rows) {
    const t = String(r.content_type ?? '').trim();
    const id = String(r.content_id ?? '').trim();
    if (!id) continue;
    if (t === 'noticia' || t === 'comunidad') {
      if (!ids[t].includes(id)) ids[t].push(id);
    } else if (t === 'listing' || t === 'auction') {
      if (!ids.listing.includes(id)) ids.listing.push(id);
    }
  }

  const tasks = [];

  if (ids.noticia.length) {
    tasks.push(
      supabase
        .from('noticias_musica')
        .select('id,titulo,extracto,imagen_url,publicado,categoria')
        .in('id', ids.noticia)
        .then(({ data, error }) => {
          if (error) return;
          for (const row of data ?? []) {
            map.set(`noticia:${row.id}`, {
              title: String(row.titulo ?? '').trim() || 'Sin título',
              subtitle: [row.categoria, row.publicado ? null : 'no publicada'].filter(Boolean).join(' · ') || null,
              extracto: row.extracto ? String(row.extracto).trim() : null,
              imageUrl: row.imagen_url ? String(row.imagen_url) : null,
            });
          }
        }),
    );
  }

  if (ids.comunidad.length) {
    tasks.push(
      supabase
        .from('comunidad_publicaciones')
        .select('id,title,publicacion_tipo,cuerpo,aprobada,is_hidden')
        .in('id', ids.comunidad)
        .then(({ data, error }) => {
          if (error) return;
          for (const row of data ?? []) {
            const c = row.cuerpo && typeof row.cuerpo === 'object' ? row.cuerpo : {};
            const place = c.place ? String(c.place).trim() : '';
            const fecha = c.fecha ? String(c.fecha).trim() : '';
            map.set(`comunidad:${row.id}`, {
              title: String(row.title ?? '').trim() || 'Sin título',
              subtitle: [
                row.publicacion_tipo ? String(row.publicacion_tipo) : null,
                place,
                fecha,
                row.aprobada ? null : 'no aprobada',
                row.is_hidden ? 'oculta' : null,
              ]
                .filter(Boolean)
                .join(' · ') || null,
              extracto: c.descripcion ? String(c.descripcion).trim().slice(0, 280) : null,
              imageUrl: pickComunidadImage(c),
            });
          }
        }),
    );
  }

  if (ids.listing.length) {
    tasks.push(
      supabase
        .from('listings')
        .select('id,title,artist,price,format,status,is_auction,cover_url')
        .in('id', ids.listing)
        .then(({ data, error }) => {
          if (error) return;
          for (const row of data ?? []) {
            const fields = listingPreviewFields(row);
            if (row.is_auction) {
              map.set(`auction:${row.id}`, fields);
            }
            map.set(`listing:${row.id}`, fields);
          }
        }),
    );
  }

  await Promise.all(tasks);

  for (const r of rows) {
    const key = queuePreviewKey(r.content_type, r.content_id);
    if (!map.has(key)) {
      map.set(key, {
        title: 'Contenido no encontrado',
        subtitle: String(r.content_id),
        missing: true,
      });
    }
  }

  return map;
}
